import { useId, useState, cloneElement, isValidElement } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Car, User, Star, Warning, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";

export const TIME_SLOTS = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
});

export const timeLabel = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
};

export const imgSrc = (u) =>
  !u ? "" : u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u;

/**
 * Label + inline error/hint wrapper.
 *
 * The label is associated with its control: a generated id goes on `htmlFor`
 * and is cloned onto the child, so every caller gets the association for free.
 * A child that already carries an `id` keeps it. Radix `Select` has no DOM
 * element of its own, so those callers pass a function child and put the id on
 * `SelectTrigger` themselves.
 */
export const Field = ({ label, required, hint, error, children, className = "" }) => {
  const generated = `fld-${useId().replace(/:/g, "")}`;
  const own = isValidElement(children) ? children.props.id : undefined;
  // The id is only injected into a child that provably lands it on a form
  // control: a native control, or one of the wrappers below that spread their
  // props onto one. Anything else (a positioning `<div>`, a picker component
  // that ignores unknown props) would leave `htmlFor` pointing at a wrapper or
  // at nothing at all, which is a worse lie than no association. Those callers
  // keep a plain label, exactly as before. Use the function-child form to
  // associate a control this cannot reach.
  const tag = isValidElement(children) ? children.type : null;
  // `MoneyInput` is declared further down the file; this comparison only runs
  // at render time, so the reference is resolved by then.
  const takesId = typeof tag === "string"
    ? ["input", "textarea", "select"].includes(tag)
    : tag === Input || tag === Textarea || tag === MoneyInput;
  const id = own || generated;
  const errorId = error ? `${id}-error` : undefined;
  const extra = { id };
  if (errorId && !(isValidElement(children) && children.props["aria-describedby"])) {
    extra["aria-describedby"] = errorId;
    extra["aria-invalid"] = true;
  }
  const control = typeof children === "function"
    ? children({ id, ...extra })
    : isValidElement(children) && takesId ? cloneElement(children, extra) : children;
  const associated = typeof children === "function" || takesId;
  return (
    <div className={className}>
      {label && (
        <Label htmlFor={associated ? id : undefined} className="label-mono mb-2 block">
          {label}{required && <span className="text-primary"> *</span>}
        </Label>
      )}
      {control}
      {error ? (
        <p id={errorId} className="text-xs text-red-400 mt-1.5 flex items-center gap-1.5"><Warning size={12} />{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>
      ) : null}
    </div>
  );
};

/** Date + 5-minute time picker. `min` clamps both the date and the time list. */
export const DateTimeField = ({ label, value, onChange, min, required, error, hint, testid }) => {
  const [d, t] = String(value || "").split("T");
  const time = (t || "").slice(0, 5);
  const [minD, minT] = String(min || "").split("T");
  const sameDayAsMin = min && d && d === minD;
  const grid = sameDayAsMin ? TIME_SLOTS.filter((s) => s >= (minT || "").slice(0, 5)) : TIME_SLOTS;
  // Keep an off-grid time (e.g. 6:50 PM on a duplicated booking) selectable.
  const slots = time && !grid.includes(time) ? [...grid, time].sort() : grid;
  const emit = (nd, nt) => {
    if (!nd) return onChange("");
    onChange(`${nd}T${nt || "00:00"}`);
  };
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      {(f) => (
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          id={f.id}
          type="date"
          value={d || ""}
          min={minD || undefined}
          onChange={(e) => emit(e.target.value, time)}
          className="flex-1"
          data-testid={`${testid}-date`}
        />
        <Select value={slots.includes(time) ? time : ""} onValueChange={(nt) => emit(d, nt)}>
          <SelectTrigger className="sm:w-40" data-testid={`${testid}-time`}>
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {slots.map((slot) => (
              <SelectItem key={slot} value={slot}>{timeLabel(slot)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      )}
    </Field>
  );
};

/** Money input with a gold $ affix. */
export const MoneyInput = ({ id, value, onChange, testid, placeholder = "0.00", disabled }) => (
  <div className="relative">
    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary text-sm">$</span>
    <Input
      id={id}
      type="number"
      inputMode="decimal"
      min="0"
      step="0.01"
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-8 money"
      data-testid={testid}
    />
  </div>
);

/** Photo card for the availability-aware vehicle picker. */
export const VehicleCard = ({ v, selected, booked, onSelect, showOwner, testid }) => (
  <button
    type="button"
    onClick={() => onSelect(v.id)}
    data-testid={testid}
    aria-pressed={selected}
    className={`text-left border rounded-2xl overflow-hidden transition-colors duration-150 ${
      selected ? "border-primary bg-primary/[0.07]" : booked ? "border-red-500/30 bg-card hover:border-red-500/60" : "border-border bg-card hover:border-primary/40"
    }`}
  >
    <div className="h-24 sm:h-28 bg-white/[0.03] relative flex items-center justify-center overflow-hidden">
      {v.image_url ? (
        <img src={imgSrc(v.image_url)} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" />
      ) : (
        <Car size={26} className="text-muted-foreground/50" />
      )}
      {booked && (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/85 text-white text-[10px] uppercase tracking-wider">
          <Warning size={10} weight="fill" /> Booked
        </span>
      )}
      {selected && (
        <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          <CheckCircle size={13} weight="fill" />
        </span>
      )}
    </div>
    <div className="p-3">
      <div className="font-heading text-sm truncate">{v.make} {v.model}</div>
      <div className="text-[11px] text-muted-foreground truncate mt-0.5">
        {[v.year, v.vehicle_class, v.license_plate].filter(Boolean).join(" · ") || "—"}
      </div>
      {showOwner && v.ownership === "third_party" && (
        <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/12 border border-amber-500/25 text-amber-400 text-[10px] uppercase tracking-wider">
          {v.owner_name || "3rd party"}
        </span>
      )}
    </div>
  </button>
);

/** Driver card with a busy/free badge for the selected slot. */
export const DriverCard = ({ d, selected, busy, busyLabel, onSelect, testid }) => (
  <button
    type="button"
    onClick={() => onSelect(d.id)}
    data-testid={testid}
    aria-pressed={selected}
    className={`text-left border rounded-2xl p-3 flex items-center gap-3 transition-colors duration-150 ${
      selected ? "border-primary bg-primary/[0.07]" : "border-border bg-card hover:border-primary/40"
    }`}
  >
    <span className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
      <User size={16} className="text-muted-foreground" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-heading text-sm truncate">{d.first_name} {d.last_name}</span>
      <span className="block text-[11px] text-muted-foreground truncate">
        {d.phone || d.email || "—"}{d.rating ? ` · ${d.rating}★` : ""}
      </span>
    </span>
    {busy ? (
      <span className="shrink-0 px-2 py-0.5 rounded-full bg-red-500/12 border border-red-500/25 text-red-400 text-[10px] uppercase tracking-wider" title={busyLabel}>Busy</span>
    ) : (
      <span className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-500/12 border border-emerald-500/25 text-emerald-400 text-[10px] uppercase tracking-wider">Free</span>
    )}
  </button>
);

/** Text input with recent-value quick picks underneath. */
export const LocationInput = ({ label, value, onChange, recents = [], placeholder, testid, required, error }) => (
  <Field label={label} required={required} error={error}>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    {recents.length > 0 && (
      <div className="flex gap-1.5 flex-wrap mt-2">
        {recents.slice(0, 4).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            className="px-2.5 py-1 rounded-full border border-border text-[11px] text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors truncate max-w-[180px]"
            data-testid={`${testid}-recent`}
          >
            {r}
          </button>
        ))}
      </div>
    )}
  </Field>
);

/** Collapsible "More options" block for rarely-used fields. */
export const MoreOptions = ({ children, label = "More options", testid = "more-options" }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border/60 pt-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className="label-mono hover:text-foreground transition-colors flex items-center gap-2"
      >
        {open ? "− Hide" : "+ Show"} {label}
      </button>
      {open && <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
};

export const SearchBox = ({ value, onChange, placeholder, testid }) => (
  <div className="relative">
    <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-10 rounded-full" data-testid={testid} />
  </div>
);

export const RatingStars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5 text-primary">
    {[...Array(Math.round(Number(n) || 0))].map((_, i) => <Star key={i} size={10} weight="fill" />)}
  </span>
);
